import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PaperPlaneTilt, Plus, FilePdf, Trash, X, MagnifyingGlass, Buildings, FileText, Scales, Receipt, Robot, Spinner, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import SignatarioDownloadModal from "@/components/SignatarioDownloadModal";

const TIPOS = [
  { v: "", l: "Todos" },
  { v: "contrato", l: "Contrato" },
  { v: "execucao", l: "Nota Fiscal" },
  { v: "aditivo", l: "Aditivo" },
];

const tipoIcon = (t) => {
  if (t === "aditivo") return <Scales size={12} weight="duotone" />;
  if (t === "execucao") return <Receipt size={12} weight="duotone" />;
  return <FileText size={12} weight="duotone" />;
};

const tipoLabel = (t) => TIPOS.find((x) => x.v === t)?.l || t;

export default function Despachos() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [tipo, setTipo] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [downloading, setDownloading] = useState(null);  // despacho selecionado para download

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (tipo) params.documento_tipo = tipo;
    api.get("/despachos", { params }).then((r) => { setItems(r.data.items); setTotal(r.data.total); });
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [search, tipo]);

  const excluir = async (d) => {
    if (!window.confirm(`Excluir o despacho ${d.numero}? Esta ação é irreversível.`)) return;
    try {
      await api.delete(`/despachos/${d.id}`);
      toast.success("Despacho excluído");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase-label flex items-center gap-1.5">
            <PaperPlaneTilt size={12} weight="duotone" className="text-[#F59E0B]" />
            Gestão de Contratos
          </div>
          <h1 className="font-heading text-4xl tracking-tight mt-2">Despacho de Documentos</h1>
          <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl">
            Encaminhe contratos, notas fiscais e aditivos à <strong className="text-[#E9EEF5]">Procuradoria Jurídica Municipal</strong> para
            análise e emissão de parecer, conforme fluxo administrativo da Lei 14.133/2021.
          </p>
        </div>
        <button data-testid="new-despacho" onClick={() => setShowForm(true)}
                className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium flex items-center gap-2">
          <Plus size={16} weight="bold" /> Novo despacho
        </button>
      </div>

      <div className="tce-card p-5">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[240px]">
            <label className="uppercase-label block mb-2">Buscar</label>
            <div className="relative">
              <MagnifyingGlass size={14} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input data-testid="despachos-search" value={search}
                     onChange={(e) => setSearch(e.target.value)}
                     placeholder="Nº do despacho, contrato, assunto…"
                     className="tce-input w-full pl-9 pr-3 py-2.5 rounded-md text-sm" />
            </div>
          </div>
          <div>
            <label className="uppercase-label block mb-2">Tipo de documento</label>
            <select data-testid="despachos-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}
                    className="tce-input px-3 py-2.5 rounded-md text-sm">
              {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
        </div>
        <div className="text-xs text-[#94A3B8] mt-3">{total} despacho(s)</div>
      </div>

      <div className="tce-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#1E293B]">
              <tr className="text-left">
                <th className="px-5 py-4 uppercase-label">Nº</th>
                <th className="px-5 py-4 uppercase-label">Assunto</th>
                <th className="px-5 py-4 uppercase-label">Tipo · Referência</th>
                <th className="px-5 py-4 uppercase-label">Contrato</th>
                <th className="px-5 py-4 uppercase-label">Município</th>
                <th className="px-5 py-4 uppercase-label">Emitido por</th>
                <th className="px-5 py-4 uppercase-label">Data</th>
                <th className="px-5 py-4 uppercase-label text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-[#94A3B8]">Nenhum despacho emitido ainda.</td></tr>
              ) : items.map((d, i) => (
                <tr key={d.id} data-testid={`despacho-row-${i}`} className="border-t border-[#334155] hover:bg-[#1E293B]/60">
                  <td className="px-5 py-4 font-mono text-[#F59E0B]">{d.numero}</td>
                  <td className="px-5 py-4 text-[#E9EEF5] max-w-[280px] truncate">{d.assunto}</td>
                  <td className="px-5 py-4 text-xs">
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#F59E0B]/15 text-[#F59E0B]">
                      {tipoIcon(d.documento_tipo)} {tipoLabel(d.documento_tipo)}
                    </span>
                    <div className="text-[10px] text-[#94A3B8] mt-1">{d.documento_ref}</div>
                  </td>
                  <td className="px-5 py-4">
                    <Link to={`/contratos/${d.contract_id}`} className="font-mono text-[#F59E0B] hover:text-[#FCD34D]">{d.contract_numero}</Link>
                  </td>
                  <td className="px-5 py-4 text-[#94A3B8]">
                    <span className="flex items-center gap-1.5">
                      <Buildings size={12} weight="duotone" className="text-[#F59E0B]" />
                      {d.municipio_nome}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs text-[#94A3B8]">{d.emitido_por_nome}</td>
                  <td className="px-5 py-4 text-xs text-[#94A3B8]">{new Date(d.created_at).toLocaleDateString("pt-BR")}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid={`despacho-editor-${i}`}
                        onClick={() => navigate(`/despachos/${d.id}/editor`)}
                        title="Abrir editor inteligente (revisão com IA)"
                        className="p-1.5 rounded text-[#94A3B8] hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors"
                      >
                        <PencilSimple size={14} weight="duotone" />
                      </button>
                      <button
                        data-testid={`despacho-pdf-${i}`}
                        onClick={() => setDownloading(d)}
                        title="Baixar PDF oficial do despacho"
                        className="p-1.5 rounded text-[#94A3B8] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors"
                      >
                        <FilePdf size={14} weight="duotone" />
                      </button>
                      <button
                        data-testid={`despacho-delete-${i}`}
                        onClick={() => excluir(d)}
                        title="Excluir despacho"
                        className="p-1.5 rounded text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
                      >
                        <Trash size={14} weight="duotone" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <DespachoForm
          onClose={() => setShowForm(false)}
          onSaved={(created) => {
            setShowForm(false);
            load();
            // Redireciona para o Editor Inteligente para revisão antes do PDF
            if (created?.id) navigate(`/despachos/${created.id}/editor`);
          }}
        />
      )}

      {downloading && (
        <SignatarioDownloadModal
          municipioId={downloading.municipio_id}
          urlBase={`/despachos/${downloading.id}/pdf`}
          filename={`despacho_${(downloading.numero || "").replace(/[^\w.-]/g, "_")}.pdf`}
          title="Despacho → Procuradoria Jurídica"
          subtitle={`${downloading.numero} · ${downloading.assunto}`}
          onClose={() => setDownloading(null)}
        />
      )}
    </div>
  );
}

function DespachoForm({ onClose, onSaved, prefill }) {
  const [contracts, setContracts] = useState([]);
  const [aditivos, setAditivos] = useState([]);
  const [execucoes, setExecucoes] = useState([]);
  const [form, setForm] = useState({
    contract_id: prefill?.contract_id || "",
    documento_tipo: prefill?.documento_tipo || "contrato",
    documento_id: prefill?.documento_id || "",
    assunto: "",
    texto: "",
    numero: "",
  });
  const [contextoIA, setContextoIA] = useState("");
  const [gerandoIA, setGerandoIA] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/contracts?limit=500").then((r) => setContracts(r.data.items || []));
  }, []);

  // Sincroniza documento_id ao mudar contrato/tipo
  useEffect(() => {
    if (!form.contract_id) { setAditivos([]); setExecucoes([]); return; }
    if (form.documento_tipo === "contrato") {
      setForm((f) => ({ ...f, documento_id: form.contract_id }));
    }
    if (form.documento_tipo === "aditivo") {
      api.get(`/contracts/${form.contract_id}/aditivos`).then((r) => setAditivos(r.data || []));
    }
    if (form.documento_tipo === "execucao") {
      api.get(`/contracts/${form.contract_id}/execucoes`).then((r) => setExecucoes(r.data?.execucoes || []));
    }
  }, [form.contract_id, form.documento_tipo]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contract_id) { toast.error("Selecione o contrato"); return; }
    if (!form.documento_id) { toast.error("Selecione o documento específico"); return; }
    if (!form.assunto.trim() || !form.texto.trim()) { toast.error("Assunto e texto são obrigatórios"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/despachos", form);
      toast.success("Despacho salvo. Abrindo Editor Inteligente…");
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao emitir");
    } finally {
      setSaving(false);
    }
  };

  const gerarIA = async () => {
    if (!form.contract_id) { toast.error("Selecione o contrato primeiro"); return; }
    if (!form.documento_id) { toast.error("Selecione o documento específico primeiro"); return; }
    setGerandoIA(true);
    try {
      const { data } = await api.post("/despachos/ai-suggest", {
        documento_tipo: form.documento_tipo,
        documento_id: form.documento_id,
        contract_id: form.contract_id,
        contexto_usuario: contextoIA || null,
        assunto: form.assunto || null,
      });
      setForm((f) => ({ ...f, assunto: data.assunto || f.assunto, texto: data.texto || f.texto }));
      toast.success("Despacho sugerido pela IA — revise e edite se necessário");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar com IA");
    } finally {
      setGerandoIA(false);
    }
  };

  const contractSelected = contracts.find((c) => c.id === form.contract_id);

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tce-card w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
          <div>
            <div className="uppercase-label flex items-center gap-1.5">
              <PaperPlaneTilt size={12} weight="duotone" className="text-[#F59E0B]" />
              Emitir despacho
            </div>
            <div className="font-heading text-xl mt-1">Encaminhamento à Procuradoria Jurídica</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#334155] text-[#94A3B8]" data-testid="despacho-form-close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="uppercase-label block mb-2">Contrato *</label>
              <select data-testid="despacho-contract" required
                      value={form.contract_id}
                      onChange={(e) => setForm({ ...form, contract_id: e.target.value, documento_id: "" })}
                      className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
                <option value="">Selecione um contrato…</option>
                {contracts.map((c) => (
                  <option key={c.id} value={c.id}>{c.numero} — {(c.objeto || "").slice(0, 60)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="uppercase-label block mb-2">Tipo do documento *</label>
              <select data-testid="despacho-tipo-doc" required
                      value={form.documento_tipo}
                      onChange={(e) => setForm({ ...form, documento_tipo: e.target.value, documento_id: e.target.value === "contrato" ? form.contract_id : "" })}
                      className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
                <option value="contrato">Contrato</option>
                <option value="execucao">Nota Fiscal / Execução</option>
                <option value="aditivo">Termo Aditivo</option>
              </select>
            </div>
          </div>

          {form.documento_tipo === "aditivo" && form.contract_id && (
            <div>
              <label className="uppercase-label block mb-2">Aditivo específico *</label>
              <select data-testid="despacho-aditivo" required
                      value={form.documento_id}
                      onChange={(e) => setForm({ ...form, documento_id: e.target.value })}
                      className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
                <option value="">
                  {aditivos.length === 0 ? "Nenhum aditivo cadastrado neste contrato" : "Selecione o aditivo…"}
                </option>
                {aditivos.map((a) => (
                  <option key={a.id} value={a.id}>Aditivo {a.numero} — {a.tipo_descricao || a.tipo}</option>
                ))}
              </select>
            </div>
          )}

          {form.documento_tipo === "execucao" && form.contract_id && (
            <div>
              <label className="uppercase-label block mb-2">Nota Fiscal / Execução específica *</label>
              <select data-testid="despacho-execucao" required
                      value={form.documento_id}
                      onChange={(e) => setForm({ ...form, documento_id: e.target.value })}
                      className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
                <option value="">
                  {execucoes.length === 0 ? "Nenhuma NF registrada neste contrato" : "Selecione a NF…"}
                </option>
                {execucoes.map((ex) => (
                  <option key={ex.id} value={ex.id}>
                    NF {ex.numero_nota_fiscal || "sem número"} — R$ {(ex.valor || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="uppercase-label block mb-2">Nº do despacho (opcional)</label>
            <input data-testid="despacho-numero" value={form.numero}
                   onChange={(e) => setForm({ ...form, numero: e.target.value })}
                   placeholder="Deixe em branco para autonumerar (ex: DP-2026-0001)"
                   className="tce-input w-full px-3 py-2.5 rounded-md text-sm font-mono" />
          </div>

          <div className="rounded-md bg-[#8B5CF6]/5 border border-[#8B5CF6]/30 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Robot size={20} weight="duotone" className="text-[#8B5CF6] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-[#E9EEF5] mb-1">Geração assistida por IA (Claude Sonnet 4.5)</div>
                <div className="text-xs text-[#94A3B8]">
                  A IA elabora o assunto e o texto formal do despacho com base no documento selecionado. Adicione abaixo suas <strong className="text-[#FCD34D]">sugestões e pontos específicos</strong> a serem abordados. <strong className="text-[#FCD34D]">A geração leva ~20-30 segundos.</strong>
                </div>
              </div>
            </div>
            <div>
              <label className="uppercase-label block mb-2">Sugestões / pontos a abordar (opcional)</label>
              <textarea
                data-testid="despacho-contexto-ia"
                rows={3}
                value={contextoIA}
                onChange={(e) => setContextoIA(e.target.value)}
                placeholder="Ex.: verificar cláusula de reajuste, aplicar índice IPCA, dúvida sobre limite de 25%, necessidade de nova pesquisa de preços…"
                className="tce-input w-full px-3 py-2.5 rounded-md text-sm"
              />
            </div>
            <button
              type="button"
              data-testid="despacho-gerar-ia"
              onClick={gerarIA}
              disabled={gerandoIA || !form.documento_id}
              className="px-4 py-2 rounded-md bg-[#8B5CF6] text-white hover:bg-[#7C3AED] font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {gerandoIA ? <Spinner size={14} className="animate-spin" /> : <Robot size={14} weight="bold" />}
              {gerandoIA ? "Gerando com IA…" : (form.texto ? "Regenerar com IA" : "Gerar despacho com IA")}
            </button>
          </div>

          <div>
            <label className="uppercase-label block mb-2">Assunto *</label>
            <input data-testid="despacho-assunto" required
                   value={form.assunto}
                   onChange={(e) => setForm({ ...form, assunto: e.target.value })}
                   placeholder="Ex.: Solicitação de parecer sobre reajuste anual do contrato"
                   className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
          </div>

          <div>
            <label className="uppercase-label block mb-2">Texto do despacho *</label>
            <textarea data-testid="despacho-texto" required rows={7}
                      value={form.texto}
                      onChange={(e) => setForm({ ...form, texto: e.target.value })}
                      placeholder="Exponha o fato, a dúvida ou a matéria a ser analisada pela Procuradoria Jurídica…"
                      className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
          </div>

          <div className="rounded-md bg-[#F59E0B]/5 border border-[#F59E0B]/30 p-3 text-xs text-[#FCD34D]">
            <strong>Destino:</strong> Procuradoria Jurídica Municipal{contractSelected?.municipio_nome ? ` de ${contractSelected.municipio_nome}` : ""}.
            O despacho será registrado no sistema com a data e o emissor atual, e ficará disponível para geração de PDF oficial.
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-sm">
              Cancelar
            </button>
            <button data-testid="despacho-form-save" disabled={saving}
                    className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm disabled:opacity-50 flex items-center gap-2">
              <PaperPlaneTilt size={14} weight="bold" /> {saving ? "Emitindo…" : "Emitir despacho"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
